import type { MenuDataItem } from '@ant-design/pro-layout/es/typing';
import type { BaseMenuProps } from '@ant-design/pro-layout/es/components/SiderMenu/BaseMenu';
import type { SiderMenuProps } from '@ant-design/pro-layout/es/components/SiderMenu/SiderMenu';
import { Menu } from 'antd';
import { history, useLocation } from '@umijs/max';
import * as Icons from '@ant-design/icons';
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

type SiderWithMatch = SiderMenuProps & { matchMenuKeys?: string[] };

function getIcon(iconName?: string | React.ReactNode) {
  if (!iconName) return null;
  if (React.isValidElement(iconName)) return iconName;
  if (typeof iconName === 'string') {
    const PascalCaseName = iconName
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
    const IconComp = (Icons as any)[`${PascalCaseName}Outlined`];
    if (IconComp && typeof IconComp === 'function') {
      return React.createElement(IconComp as React.ComponentType);
    }
    // 开发环境提示未解析的图标名。本文件按名字拼串动态取图标，图标名有一部分来自
    // 后端菜单的 icon 字段、无法从代码枚举，漏配时这里会打出来。生产环境不输出。
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[menu-icon] 未解析到图标：icon="${iconName}" → 期望 @ant-design/icons 的 ${PascalCaseName}Outlined`,
      );
    }
  }
  return null;
}

function getMenuText(
  item: MenuDataItem,
  formatMessage: BaseMenuProps['formatMessage'],
  menu: BaseMenuProps['menu'],
  menuTextRender: BaseMenuProps['menuTextRender'],
  baseProps: BaseMenuProps,
): React.ReactNode {
  let text: React.ReactNode = item.name;
  if (item.locale && menu?.locale !== false && formatMessage) {
    text = formatMessage({ id: item.locale as string, defaultMessage: item.name });
  } else if (formatMessage && typeof item.name === 'string' && item.name.includes('.')) {
    text = formatMessage({ id: item.name, defaultMessage: item.name });
  }
  if (menuTextRender) {
    return menuTextRender(item, text, baseProps);
  }
  return text;
}

function filterVisible(items?: MenuDataItem[]): MenuDataItem[] {
  if (!items?.length) return [];
  return items.filter((c) => c && c.name && !c.hideInMenu);
}

// ── 抽屉面板内容渲染 ──
function renderDrawerContent(
  menu: MenuDataItem,
  baseProps: SiderWithMatch & BaseMenuProps,
  onItemClick: (path?: string) => void,
): React.ReactNode {
  const { formatMessage, menu: menuConfig, menuTextRender } = baseProps;
  const children = filterVisible(menu.children);
  if (!children.length) return null;

  const hasThirdLevelItems = children.filter(
    (item) => item.children && item.children.length > 0,
  );
  const noThirdLevelItems = children.filter(
    (item) => !item.children || item.children.length === 0,
  );

  return (
    <>
      {/* 有三级菜单的二级菜单 - 作为分组展示 */}
      {hasThirdLevelItems.map((secondLevel) => {
        const displayTitle =
          getMenuText(secondLevel, formatMessage, menuConfig, menuTextRender, baseProps) ??
          (secondLevel as { groupTitle?: string }).groupTitle;
        const thirdLevelItems = filterVisible(secondLevel.children);

        return (
          <div
            key={secondLevel.key || `group-${String(displayTitle)}`}
            className="menu-drawer-group"
          >
            <div className="menu-drawer-group-title">{displayTitle}</div>
            <div className="menu-drawer-group-items">
              {thirdLevelItems.map((thirdLevel) => {
                const l3Title = getMenuText(thirdLevel, formatMessage, menuConfig, menuTextRender, baseProps);
                return (
                  <div
                    key={thirdLevel.key}
                    className="menu-drawer-item"
                    onClick={() => onItemClick(thirdLevel.path)}
                  >
                    {getIcon(thirdLevel.icon)}
                    {l3Title}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* 没有三级菜单的二级菜单 - 直接平铺 */}
      {noThirdLevelItems.length > 0 && (
        <div className="menu-drawer-group">
          <div className="menu-drawer-group-items">
            {noThirdLevelItems.map((secondLevel) => {
              const displayTitle =
                getMenuText(secondLevel, formatMessage, menuConfig, menuTextRender, baseProps) ??
                (secondLevel as { groupTitle?: string }).groupTitle;
              return (
                <div
                  key={secondLevel.key || `item-${String(displayTitle)}`}
                  className="menu-drawer-item"
                  onClick={() => onItemClick(secondLevel.path)}
                >
                  {getIcon(secondLevel.icon)}
                  {displayTitle}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * 一级菜单的选中态必须是**单值**。
 *
 * ProLayout 的 matchMenuKeys 是 fullKeys 语义 —— `getMatchMenu(pathname, menuData, true)`
 * 会把当前路径命中的项**以及它们的祖先链**一并返回（见
 * node_modules/@ant-design/pro-layout/es/ProLayout.js）。而 antd Menu 的 selectedKeys
 * 是**集合语义**，原样下发会让多个一级项同时高亮。
 *
 * 典型触发场景：菜单挂在 A 域、URL 落在 B 域。例如「委外订单」的 URL 是
 * `/sales/outsourcing/order`（前缀命中一级「销售管理」的 `/sales`），而它在菜单树里
 * 挂在「采购管理」下（祖先链里带采购管理的一级 key）⇒ 两个一级项同时选中。
 *
 * 取法：找到当前路径**匹配最深**的那个节点，返回它**最外层祖先**的 key，只留一个。
 * 匹配用前缀（`/sales` 要能命中 `/sales/outsourcing/order`），并取最长者，
 * 这样「同前缀的更深菜单」会赢。路径不在菜单树里（详情页、独立页等）⇒ 返回 null，
 * 调用方退回原 matchMenuKeys，保持既有行为。
 */
function topLevelKeyOfPath(
  menuData: MenuDataItem[] | undefined,
  pathname: string,
): string | null {
  let bestPath = '';
  let bestTopKey: string | null = null;
  const walk = (nodes: MenuDataItem[], topKey: string | null) => {
    for (const node of nodes) {
      // node.key 在 MenuDataItem 上是可选的，这里收敛成 string | null，
      // 与 topKey 的语义一致（null = 还没有祖先，取到最外层就固定下来）。
      const currentTopKey: string | null =
        topKey ?? (typeof node.key === 'string' ? node.key : null);
      const nodePath = typeof node.path === 'string' && node.path.startsWith('/')
        ? node.path
        : undefined;
      if (nodePath && pathname.startsWith(nodePath) && nodePath.length > bestPath.length) {
        bestPath = nodePath;
        bestTopKey = currentTopKey;
      }
      if (node.children?.length) walk(node.children, currentTopKey);
    }
  };
  walk(menuData || [], null);
  return bestTopKey;
}

// ═══════════════════════════════════════════
//   MegaMenuContent — 点击展开抽屉面板
// ═══════════════════════════════════════════
const MegaMenuContent: React.FC<SiderWithMatch & BaseMenuProps> = (props) => {
  const {
    menuData,
    matchMenuKeys,
    className,
    style,
    menuProps,
    collapsed,
  } = props;

  // ── 抽屉状态 ──
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelLeft, setPanelLeft] = useState(210);
  const [panelTop, setPanelTop] = useState(52); // 抽屉顶部坐标
  const [panelBottom, setPanelBottom] = useState<number | null>(null); // null=向下伸展模式, number=向上伸展模式
  const HEADER_HEIGHT = 52;
  const GAP = 4; // 抽屉与页面底部的间距
  const BOTTOM_ALIGN_RATIO = 0.7; // 视口后 30%（≥70%）：抽屉贴底向上伸展；其余：跟随菜单项向下伸展

  // 侧边栏右边缘位置（跟随展开/折叠变化）
  useLayoutEffect(() => {
    const update = () => {
      const sider = document.querySelector('.ant-pro-sider');
      if (sider) setPanelLeft(sider.getBoundingClientRect().right);
    };
    update();
    const sider = document.querySelector('.ant-pro-sider');
    if (!sider) return;
    const ro = new ResizeObserver(update);
    ro.observe(sider);
    return () => ro.disconnect();
  }, [collapsed]);

  // 监听面包屑点击事件 → 打开对应抽屉
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key) {
        setActiveKey(detail.key);
      }
    };
    window.addEventListener('menu:openDrawer', handler);
    return () => window.removeEventListener('menu:openDrawer', handler);
  }, []);

  // ── 计算抽屉面板的垂直位置（两分区+内容感知策略） ──
  //
  //  视口 0% ─────────────────────────────────── 100%
  //  │        跟随菜单项（0~70%）         │  居底区（70~100%） │
  //  │  top=菜单项.top，向下伸展           │  根据内容量决定     │
  //
  //  居底区细化：
  //    - 最后一个菜单 → 无条件贴底
  //    - 子菜单内容超出下方可用空间 → 贴底向上伸展（避免滚动条）
  //    - 子菜单少且非末项 → 跟随菜单项
  const calcPanelPosition = useCallback(
    (menuItemElement: HTMLElement, isLast: boolean, childCount: number) => {
      const rect = menuItemElement.getBoundingClientRect();
      const menuItemTop = rect.top;
      const viewportHeight = window.innerHeight;
      const ratio = menuItemTop / viewportHeight;

      if (ratio < BOTTOM_ALIGN_RATIO) {
        // 跟随菜单项，向下伸展
        setPanelTop(Math.max(HEADER_HEIGHT, menuItemTop));
        setPanelBottom(null);
        return;
      }

      // ── 居底区（≥70%）→ 内容感知决策 ──
      // 预估抽屉内容高度：每行 ~38px + 标题栏 ~80px
      const estimatedContentHeight = childCount * 38 + 80;
      const availableBelow = viewportHeight - menuItemTop - GAP;
      const needsScrollbar = estimatedContentHeight > availableBelow;

      if (isLast || needsScrollbar) {
        // 最后一个，或内容溢出 → 贴底向上伸展
        setPanelBottom(GAP);
      } else {
        // 非末项、内容少 → 跟随菜单项
        setPanelTop(Math.max(HEADER_HEIGHT, menuItemTop));
        setPanelBottom(null);
      }
    },
    [],
  );

  // 预计算：末位可见菜单的 key（用于居底区判断）
  const lastVisibleMenuKey = useMemo(() => {
    const visible = menuData?.filter((m) => filterVisible(m.children).length > 0);
    return visible?.length ? visible[visible.length - 1].key : null;
  }, [menuData]);

  // ── 点击事件 ──
  const handleMenuClick = useCallback(
    (key: string, menuItemEl: HTMLElement) => {
      // 点击同一个一级菜单 → 切换关闭
      if (activeKey === key) {
        setActiveKey(null);
        return;
      }
      setActiveKey(key);
      const menu = menuData?.find((m) => m.key === key);
      const childCount = menu ? filterVisible(menu.children).length : 0;
      calcPanelPosition(menuItemEl, key === lastVisibleMenuKey, childCount);
    },
    [activeKey, calcPanelPosition, menuData, lastVisibleMenuKey],
  );

  const handleItemClick = useCallback((path?: string) => {
    if (path) history.push(path);
    setActiveKey(null);
  }, []);

  // ── 渲染 ──
  const activeMenu = menuData?.find((m) => m.key === activeKey);

  const mergedClassName = ['ant-pro-sider-menu', 'mega-menu-root', className]
    .filter(Boolean)
    .join(' ');

  // 选中态单值化（原因见 topLevelKeyOfPath 的注释）：抽屉打开时以 activeKey 为准，
  // 否则只取当前路径最深命中节点的最外层祖先；取不到才退回 matchMenuKeys。
  const location = useLocation();
  const selectedKeys = useMemo(() => {
    if (activeKey) return [activeKey];
    const topKey = topLevelKeyOfPath(menuData, location.pathname);
    return topKey ? [topKey] : matchMenuKeys || [];
  }, [activeKey, menuData, matchMenuKeys, location.pathname]);

  /**
   * ── T5 菜单分块：一级列表按分组聚合 ──
   * app.tsx 在 menuDataRender 里把分组信息回写到一级菜单项上
   * （__sidebarGroupKey / __sidebarGroupTitle），这里按「相邻同组」聚合，
   * 得到的顺序即 app.tsx 的组顺序（组顺序 = config/sidebarGroups.ts 的数组顺序）。
   * 名单未配置时菜单项上没有这两个字段 → 只有一个桶且无标题 → 退化为现状。
   */
  const groupBuckets = useMemo(() => {
    const list = menuData || [];
    const buckets: {
      key: string;
      title?: string;
      items: MenuDataItem[];
    }[] = [];
    for (const item of list) {
      const title = (item as { __sidebarGroupTitle?: string })
        .__sidebarGroupTitle;
      const key = (item as { __sidebarGroupKey?: string }).__sidebarGroupKey;
      const last = buckets[buckets.length - 1];
      if (last && last.title === title) {
        last.items.push(item);
      } else {
        buckets.push({
          key: key || `sidebar-group-${buckets.length}`,
          title,
          items: [item],
        });
      }
    }
    return buckets;
  }, [menuData]);

  /**
   * 分组标题：仅当「配置了 ≥2 组」且未折叠时渲染。
   * 名单为空 / 未配置 / 只有一组 → 不渲染任何分组标题（零风险退化）。
   * C / D 档由 CSS 隐藏（窄档下 ProLayout 的 collapsed 不一定为 true）。
   */
  const showGroupTitles = useMemo(() => {
    if (collapsed) return false;
    const titles = new Set(groupBuckets.map((bucket) => bucket.title).filter(Boolean));
    return titles.size > 1;
  }, [collapsed, groupBuckets]);

  const renderMenuItem = (menu: MenuDataItem) => {
    const hasChildren = filterVisible(menu.children).length > 0;
    const title = getMenuText(
      menu,
      props.formatMessage,
      props.menu,
      props.menuTextRender,
      props,
    );

    return (
      <Menu.Item
        key={menu.key}
        /*
         * 🔑 图标走 antd 的 icon 属性（而不是塞进 children）：
         * antd 会把 icon 渲染成 .ant-menu-item-icon，与 .ant-menu-title-content 平级。
         * 这样窄档（C 档 / 折叠态）隐藏 .ant-menu-title-content 时只隐藏文字、保留图标；
         * 若把图标放进 children，隐藏标题内容会把图标一起藏掉。
         */
        icon={getIcon(menu.icon)}
        onClick={(info: any) => {
          if (hasChildren) {
            handleMenuClick(
              menu.key as string,
              info.domEvent.currentTarget as HTMLElement,
            );
          }
        }}
      >
        {!collapsed && <span>{title}</span>}
      </Menu.Item>
    );
  };

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      {/* ── 一级菜单 ── */}
      <Menu
        key="mega-base-menu"
        {...menuProps}
        mode="vertical"
        inlineCollapsed={collapsed}
        className={mergedClassName}
        style={{ width: '100%', border: 'none', background: 'transparent', ...style }}
        selectedKeys={selectedKeys}
      >
        {showGroupTitles
          ? groupBuckets.map((bucket) => (
              <Menu.ItemGroup
                key={bucket.key}
                title={<div className="nav__group">{bucket.title}</div>}
              >
                {bucket.items.map(renderMenuItem)}
              </Menu.ItemGroup>
            ))
          : groupBuckets.flatMap((bucket) => bucket.items.map(renderMenuItem))}
      </Menu>

      {/* ── 抽屉面板 ── */}
      {activeKey && activeMenu && (
        <>
          {/* 半透明遮罩，仅覆盖右侧内容区 */}
          <div
            className="menu-drawer-overlay"
            style={{ left: panelLeft }}
            onClick={() => setActiveKey(null)}
          />

          <div
            ref={panelRef}
            className="menu-drawer-panel open"
            style={{
              left: panelLeft,
              top: panelBottom != null ? 'auto' : panelTop,
              bottom: panelBottom != null ? panelBottom : 'auto',
              maxHeight: panelBottom != null
                ? `calc(100vh - ${HEADER_HEIGHT}px - ${GAP}px)`
                : `calc(100vh - ${panelTop}px - ${GAP}px)`,
              overflowY: 'auto',
            }}
          >
            {/* 标题：始终显示 */}
            <div className="menu-drawer-header">
              {getMenuText(
                activeMenu,
                props.formatMessage,
                props.menu,
                props.menuTextRender,
                props,
              )}
            </div>

            {/* 内容 */}
            <div
              className={
                collapsed
                  ? 'menu-drawer-body menu-drawer-body--compact'
                  : 'menu-drawer-body'
              }
            >
              {renderDrawerContent(activeMenu, props, handleItemClick)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MegaMenuContent;
